import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * The value each animatable property settles on. `reveal` reads the start
 * values a caller gives it and looks the finish values up here.
 */
const NEUTRAL = {
  opacity: 1,
  x: 0,
  y: 0,
  xPercent: 0,
  yPercent: 0,
  scale: 1,
  scaleX: 1,
  scaleY: 1,
  rotate: 0,
  filter: 'blur(0px)',
  strokeDashoffset: 0,
};

/**
 * Build a `reveal(targets, from, vars, position)` bound to one timeline.
 *
 * It exists to keep `.from()` out of this codebase. A `from` tween works out
 * its destination by reading the element's current value at build time, which
 * is fine once and catastrophic twice: React StrictMode runs every layout
 * effect a second time, and by then the first run's tween has already written
 * its start values inline. The second tween reads `opacity: 0` as the
 * destination and dutifully animates 0 -> 0, leaving the element invisible
 * even though its timeline ran to completion.
 *
 * `fromTo` states both ends literally, so the result no longer depends on how
 * many times the effect ran or on what the previous run left behind.
 */
export function makeReveal(timeline, select) {
  return (targets, from, vars = {}, position) => {
    const elements = typeof targets === 'string' ? select(targets) : targets;
    if (!elements || elements.length === 0) return timeline;

    const to = {};
    for (const key of Object.keys(from)) {
      if (key in NEUTRAL) to[key] = NEUTRAL[key];
    }

    return timeline.fromTo(elements, from, { ...to, ...vars }, position);
  };
}

/**
 * The one headline entrance every section shares.
 *
 * Each line rises out of its own overflow-hidden mask -- slightly small,
 * slightly faded -- over a slow three seconds, so the arrival is something a
 * reader watches rather than notices. `power3.out` rather than `power4`: the
 * steeper curve spends most of the three seconds all but stopped, which read
 * as the line landing early and then sitting there.
 *
 * The stagger is a clear beat, not a ripple: the next line or word begins
 * 0.6s into the one before it, with that one well on its way up but still
 * clearly moving -- "Internships" and then "made easy.", read in order rather
 * than arriving as one stack with a slight lean.
 *
 * Spread with `reveal(selector, HEADLINE_FROM, HEADLINE_TO, at)`; pass a
 * `{ ...HEADLINE_TO, stagger }` to override the gap between lines.
 */
export const HEADLINE_FROM = { yPercent: 110, opacity: 0.4, scale: 0.97 };
export const HEADLINE_TO = { duration: 3, stagger: 0.6, ease: 'power3.out' };

/**
 * Add the hero's scroll-away to `timeline`, on `target`, over one unit of
 * timeline time from `at`.
 *
 * The same curve as `wonder-hero-zoom` in the stylesheet: scale to three and
 * blur to ten across the whole range, opacity held near full for the first
 * third so the growth is what reads first and the fade only takes over once
 * the text is already oversized. Linear on purpose -- it is meant to be
 * scrubbed by the scrollbar, and an eased scrub feels like the wheel
 * slipping. The origin sits above centre, as the hero's does, so the heading
 * grows up and out of the page rather than swelling in place.
 *
 * Blur is the one property here that cannot run on the compositor. It is
 * applied to a heading, not a section, so the repaint is a few lines of text
 * and stays cheap.
 */
export function zoomOutOnScroll(timeline, target, at = 0) {
  return timeline
    .fromTo(
      target,
      { scale: 1, filter: 'blur(0px)', transformOrigin: 'center 40%' },
      { scale: 3, filter: 'blur(10px)', duration: 1, ease: 'none' },
      at,
    )
    .fromTo(target, { opacity: 1 }, { opacity: 0.85, duration: 0.35, ease: 'none' }, at)
    .to(target, { opacity: 0, duration: 0.65, ease: 'none' }, at + 0.35);
}

/**
 * Zoom a section's heading out and away as the reader scrolls past it, the
 * way the hero's headline leaves.
 *
 * Attach the returned ref to the heading. The trigger is the heading's
 * section, and the geometry is the hero's exactly: the zoom runs from the
 * moment the section's top edge reaches the top of the viewport -- the section
 * is fully on screen and about to start leaving -- to the moment its bottom
 * edge gets there. Nothing moves while the section is arriving or sitting in
 * view. An earlier pass measured off the heading's own centre instead, and
 * with the sections a full viewport tall and their content centred, a heading
 * sits above the middle of the screen while its section is in full view, so
 * it was already half zoomed and blurred before the reader had finished with
 * it.
 *
 * Not for a pinned section: the heading is frozen in place for the whole pin,
 * so a trigger measured off it would fire at the wrong scroll. That section
 * appends `zoomOutOnScroll` to its own scrubbed timeline instead.
 */
export function useHeadingZoomOut() {
  const target = useRef(null);

  useLayoutEffect(() => {
    const element = target.current;
    if (!element) return undefined;

    const media = gsap.matchMedia();

    media.add('(prefers-reduced-motion: no-preference)', () => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: element.closest('section') ?? element,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });
      zoomOutOnScroll(timeline, element);
    });

    return () => media.revert();
  }, []);

  return target;
}

/** Where a section is on screen enough to be worth animating in. */
const ENTER = 'top 78%';

/** ...and where it has left again, mirrored so both directions read alike. */
const EXIT = 'bottom 20%';

/**
 * Play `timeline` from the top every time `trigger` comes into view.
 *
 * The page used to animate each section once per page load. Come back to it --
 * scroll down and back up, or follow a link and return -- and the section was
 * already sitting in its finished state, so most of the work in these
 * timelines was only ever seen by a reader who happened to arrive at exactly
 * the right scroll position, once.
 *
 * Both directions are handled. `onEnter` is arriving from above, the common
 * case; `onEnterBack` is scrolling back up into a section from below it, which
 * is what re-reading a page actually looks like.
 *
 * Nothing is rewound on the way out, and that is the point of using
 * `restart()`: it rewinds and plays within the same frame, so the start state
 * is never held where anyone can see it. Rewinding on exit instead -- the
 * obvious alternative -- blanks the section while it is still a visible sliver
 * at the edge of the screen.
 */
export function playWhenSeen(trigger, timeline, { start = ENTER, end = EXIT, once = false } = {}) {
  // `once` is the hero's case: it plays the first time the trigger point is
  // reached and then the section sits in its finished state for the rest of
  // the page load. No `onEnterBack`, no rewind -- the trigger is killed after
  // the first fire, so nothing can restart it.
  if (once) {
    return ScrollTrigger.create({ trigger, start, once: true, onEnter: () => timeline.play() });
  }
  return ScrollTrigger.create({
    trigger,
    start,
    end,
    onEnter: () => timeline.restart(),
    onEnterBack: () => timeline.restart(),
  });
}

/**
 * Play a section's entrance every time it reaches the viewport.
 *
 * Every marketing section animates on the same terms -- nothing moves until it
 * is on screen, it replays whenever it comes back, and `prefers-reduced-motion`
 * skips it entirely -- so that policy lives here rather than being restated in
 * each section. The reduced-motion branch is a no-op on purpose: the markup
 * already renders in its finished state, so honouring the preference means
 * declining to animate rather than animating to the same place.
 *
 * `build` is held in a ref, so callers can pass an inline arrow without
 * wrapping it in `useCallback` and without the timeline being torn down and
 * rebuilt on every render.
 *
 * Returns the ref to attach to the section element; it is both the animation
 * scope for selectors and the ScrollTrigger trigger.
 */
export function useSectionReveal(build, { start = ENTER, end = EXIT, once = false } = {}) {
  const root = useRef(null);
  const latestBuild = useRef(build);
  latestBuild.current = build;

  useLayoutEffect(() => {
    const scope = root.current;
    const media = gsap.matchMedia();

    media.add('(prefers-reduced-motion: no-preference)', () => {
      // Built paused and with no trigger attached yet. Handing `scrollTrigger`
      // to the timeline constructor creates the trigger against an *empty*
      // timeline -- it measures a duration of zero and can advance the
      // playhead before `build` has added anything. Every tween added after
      // that lands behind the playhead and renders at its start values
      // forever, which is how a section ends up with its heading revealed and
      // its buttons stuck invisible.
      const timeline = gsap.timeline({ paused: true, defaults: { overwrite: 'auto' } });

      const select = gsap.utils.selector(scope);
      latestBuild.current({ timeline, select, scope, reveal: makeReveal(timeline, select) });

      playWhenSeen(scope, timeline, { start, end, once });
    });

    return () => media.revert();
  }, [start, end, once]);

  // The webfont lands after the first layout and moves everything below it, so
  // every start position measured before then is stale. One refresh once the
  // fonts are in re-measures them all.
  useLayoutEffect(() => {
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) ScrollTrigger.refresh();
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return root;
}

/**
 * Scrub a section's content through a zoom as it crosses the viewport.
 *
 * The entrance timelines above fire once and then the section sits still,
 * which is what made the page read as a stack of slides rather than as one
 * moving thing. This is the other half: content arrives undersized, reaches
 * its true size exactly as the section centres, and keeps growing as it
 * leaves. Because it is scrubbed rather than played, it tracks the wheel both
 * ways -- scrolling back up runs it backwards.
 *
 * `start: 'top bottom'` to `end: 'bottom top'` spans the whole crossing, and
 * that range is symmetric about the section's centre whatever the section's
 * height, so progress 0.5 -- where the two tweens meet and scale is exactly
 * 1 -- always lands with the section centred.
 *
 * Only `scale` and `opacity` move. A blur here would be the honest match for
 * the reference sites, but it is the one property in this whole page that
 * cannot run on the compositor, and running it over a full-height section on
 * every scroll frame is how this turns to jank on a laptop.
 *
 * Attach the returned ref to the section's *content* wrapper, never to the
 * `<section>` itself: the sections are full-bleed, so scaling the box would
 * open a gap at its edges and show the page behind it.
 */
export function useScrollZoom({ enter = 0.9, exit = 1.08, fade = 0.35, scrub = 0.7 } = {}) {
  const target = useRef(null);

  useLayoutEffect(() => {
    const element = target.current;
    if (!element) return undefined;

    const media = gsap.matchMedia();

    media.add('(prefers-reduced-motion: no-preference)', () => {
      // Triggered on the section rather than on the element being scaled.
      // ScrollTrigger measures its trigger's position, and this element is
      // about to be scaled by the very timeline doing the measuring.
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: element.closest('section') ?? element,
          start: 'top bottom',
          end: 'bottom top',
          scrub,
        },
      });

      timeline
        .fromTo(
          element,
          { scale: enter, opacity: fade },
          { scale: 1, opacity: 1, ease: 'power2.out' },
        )
        .to(element, { scale: exit, opacity: fade, ease: 'power2.in' });
    });

    return () => media.revert();
  }, [enter, exit, fade, scrub]);

  return target;
}
